import React, { useRef } from 'react';
import { Button, MenuItem, Link, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import NextLink from 'next/link';
import {
  usePopupState,
  bindFocus,
  bindHover,
  bindMenu,
} from 'material-ui-popup-state/hooks';

import { MenuItemType } from './DesktopMenu';

export interface PopoverMenuItemProps {
  label: string;
  url: string;
  submenuItems?: MenuItemType[];
}

export function PopoverMenuItem(props: PopoverMenuItemProps) {
  const { label, url, submenuItems } = props;

  const theme = useTheme();
  const textColor = theme.palette.text.secondary;
  const hasSubItems = submenuItems && submenuItems.length > 0;

  const firstSubEntryRef = useRef<HTMLLIElement>(null);
  const parentEntryRef = useRef<HTMLButtonElement>(null);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'desktopMenu',
  });

  const handleKeyDownOnParent = (e: React.KeyboardEvent) => {
    if (e.code === 'ArrowDown') {
      e.preventDefault(); // don't scroll down the page
      // Go into the submenu and focus the first item
      if (hasSubItems) {
        popupState.open();
        firstSubEntryRef.current?.focus();
      }
    }
  };

  const handleKeyDownOnSubmenu = (e: React.KeyboardEvent) => {
    if (e.code === 'Escape') {
      // refocus the parent item
      parentEntryRef.current?.focus();
    }
  };

  return (
    <>
      <Button
        variant="text"
        color="inherit"
        sx={{
          p: '0 0.4rem',
          m: 0,
          fontSize: '1rem',
          letterSpacing: 0,
          color: textColor,
          fontWeight: 400,
          '& .MuiButton-endIcon': {
            ml: '-0.15rem',
          },
        }}
        {...bindHover(popupState)}
        {...bindFocus(popupState)}
        LinkComponent={Link}
        href={url}
        onKeyDown={handleKeyDownOnParent}
        ref={parentEntryRef}
        endIcon={hasSubItems && <ArrowDropDownIcon />}
      >
        {label}
      </Button>
      {hasSubItems && (
        <HoverMenu
          {...bindMenu(popupState)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {submenuItems!.map((submenuItem, index) => (
            // @ts-expect-error
            <MenuItem
              key={submenuItem.label}
              onClick={popupState.close}
              ref={index === 0 ? firstSubEntryRef : null}
              onKeyDown={handleKeyDownOnSubmenu}
              component={NextLink}
              href={submenuItem.url}
              sx={{
                letterSpacing: 0,
                color: textColor,
              }}
            >
              {submenuItem.label}
            </MenuItem>
          ))}
        </HoverMenu>
      )}
    </>
  );
}
